import { useRef } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { useLiveQuery } from 'dexie-react-hooks';
import { AccountingEngine } from '../services/accountingEngine';

export const RegisterSummaryView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex, activeMenuLevel }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  
  const registerType = activeMenuLevel.split('_')[2]; // e.g., 'receipt'
  const title = registerType.charAt(0).toUpperCase() + registerType.slice(1) + ' Register';

  const summaryData = useLiveQuery(async () => {
    if (!activeCompany) return [];
    const typeMap: Record<string, string> = {
      'receipt': 'Receipt',
      'payment': 'Payment',
      'sales': 'Sales',
      'purchase': 'Purchase',
      'journal': 'Journal',
      'contra': 'Contra'
    };
    const vchType = typeMap[registerType] || 'Receipt';
    return await AccountingEngine.generateRegisterSummary(activeCompany.id!, vchType, fromDate, toDate);
  }, [activeCompany, registerType, fromDate, toDate]);

  const flatItems = summaryData || [];
  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => flatItems && flatItems[selectedIndex] && handleItemSelect(flatItems[selectedIndex], selectedIndex));

  const totalVouchers = flatItems.reduce((sum, item) => sum + item.count, 0);
  const grandTotalAmount = flatItems.reduce((sum, item) => sum + (item.totalAmount || 0), 0);

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">{title}</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex justify-between border-b border-gray-300 font-bold px-2 py-1 leading-tight items-end">
          <div className="flex flex-col w-1/2">
            <span className="mb-[2px]">Particulars</span>
          </div>
          <div className="w-1/2 flex justify-end text-right">
             <div className="flex flex-col w-[300px]">
                <span className="text-center italic font-normal text-[11px]">{title.split(' ')[0]}</span>
                <span className="text-center">{activeCompany?.name}</span>
                <span className="text-center font-normal text-[11px]">For {fromDate}</span>
                <span className="text-center border-b border-gray-300 mb-1">Transactions</span>
                <div className="flex justify-between">
                   <span className="w-[80px] text-center">Total Vch</span>
                   <span className="w-[80px] text-center">(canc)</span>
                   <span className="flex-1 text-right pr-2">Total Amount</span>
                </div>
             </div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          {flatItems.map((item: any, idx: number) => (
            <div 
              key={idx}
              className={`flex justify-between px-2 py-[2px] cursor-pointer leading-tight ${selectedIndex === idx ? 'bg-[#1e8585] text-white active-row' : 'text-black'}`}
              onClick={() => handleItemSelect(item, idx)}
            >
              <span className="w-1/2">{item.name}</span>
              <div className="w-1/2 flex justify-end text-right">
                <div className="flex w-[300px] justify-between">
                   <span className="w-[80px] text-center">{item.count > 0 ? item.count : ''}</span>
                   <span className="w-[80px] text-center">{item.cancelled > 0 ? item.cancelled : ''}</span>
                   <span className="flex-1 text-right pr-2">{item.totalAmount > 0 ? item.totalAmount.toFixed(2) : ''}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between border-t border-gray-400 font-bold px-2 py-1 bg-gray-50">
          <span className="w-1/2">Grand Total</span>
          <div className="w-1/2 flex justify-end text-right">
             <div className="flex w-[300px] justify-between">
                <span className="w-[80px] text-center">{totalVouchers > 0 ? totalVouchers : ''}</span>
                <span className="w-[80px] text-center"></span>
                <span className="flex-1 text-right pr-2">{grandTotalAmount > 0 ? grandTotalAmount.toFixed(2) : ''}</span>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};
